import { useState } from 'react';
import {
    CpmButton,
    CpmCheckbox,
    CpmChip,
    CpmFilterDropdown,
    CpmTooltip,
} from '../../../components/design-system';
import type { ConfigTemplateFieldSchema } from '../../../types/configTemplate.types';
import { CONFIG_TEMPLATE_COPY } from '../configTemplateCopy';
import {
    fieldHint,
    fieldListPlaceholder,
    fieldTextPlaceholder,
    fieldTimePlaceholder,
    fieldUnit,
} from '../utils/fieldMeta';
import ExpenseCategoryList, { type ExpenseCategoryRow } from './ExpenseCategoryList';
import SegmentedToggle from './SegmentedToggle';
import SettingsInput, { PlusIcon } from './SettingsInput';
import WeekdayTimingsEditor, { type WeekdayTimingRow } from './WeekdayTimingsEditor';

interface FieldControlProps {
    field: ConfigTemplateFieldSchema;
    included: boolean;
    value: unknown;
    changed?: boolean;
    clusterTitle?: string | null;
    onIncludedChange: (included: boolean, fallback?: unknown) => void;
    onValueChange: (value: unknown) => void;
}

function defaultValue(field: ConfigTemplateFieldSchema): unknown {
    switch (field.valueKind) {
        case 'boolean':
            return false;
        case 'number':
            return 0;
        case 'string':
        case 'time':
            return '';
        case 'enum':
            return field.enumValues?.[0] ?? '';
        case 'string[]':
            return [];
        case 'object[]':
            return [];
        case 'weekdayTimings':
            return [];
        default:
            return '';
    }
}

function InfoIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="11" x2="12" y2="16.5" strokeLinecap="round" />
            <circle cx="12" cy="7.8" r="0.9" fill="currentColor" stroke="none" />
        </svg>
    );
}

function ChipSet({
    options,
    selected,
    onChange,
}: {
    options: string[];
    selected: string[];
    onChange: (next: string[]) => void;
}) {
    const picked = new Set(selected);
    return (
        <div className="flex flex-wrap" style={{ gap: 6 }}>
            {options.map((option) => {
                const on = picked.has(option);
                return (
                    <CpmChip
                        key={option}
                        variant="filter"
                        active={on}
                        onClick={() =>
                            onChange(on ? selected.filter((item) => item !== option) : [...selected, option])
                        }
                    >
                        {option}
                    </CpmChip>
                );
            })}
        </div>
    );
}

function FreeStringList({
    value,
    placeholder,
    onChange,
}: {
    value: string[];
    placeholder: string;
    onChange: (next: string[]) => void;
}) {
    const [draft, setDraft] = useState('');
    const add = () => {
        const next = draft.trim();
        if (!next) return;
        onChange([...value, next]);
        setDraft('');
    };
    return (
        <div>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
                {value.map((item, index) => (
                    <CpmChip
                        key={`${item}-${index}`}
                        variant="token"
                        onRemove={() => onChange(value.filter((_, i) => i !== index))}
                    >
                        {item}
                    </CpmChip>
                ))}
                {value.length === 0 ? (
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {CONFIG_TEMPLATE_COPY.EMPTY_LIST}
                    </span>
                ) : null}
            </div>
            <div className="flex items-center" style={{ gap: 8, marginTop: 8 }}>
                <SettingsInput
                    value={draft}
                    onChange={setDraft}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            add();
                        }
                    }}
                    placeholder={placeholder}
                    width={240}
                />
                <CpmButton
                    variant="outline"
                    size="sm"
                    leadingIcon={<PlusIcon />}
                    disabled={!draft.trim()}
                    onClick={add}
                >
                    {CONFIG_TEMPLATE_COPY.ADD}
                </CpmButton>
            </div>
        </div>
    );
}

function NameRows({
    value,
    placeholder,
    onChange,
}: {
    value: { name: string }[];
    placeholder: string;
    onChange: (next: { name: string }[]) => void;
}) {
    return (
        <FreeStringList
            value={value.map((row) => row.name).filter(Boolean)}
            placeholder={placeholder}
            onChange={(next) => onChange(next.map((name) => ({ name })))}
        />
    );
}

export default function FieldControl({
    field,
    included,
    value,
    changed = false,
    clusterTitle,
    onIncludedChange,
    onValueChange,
}: FieldControlProps) {
    const fallback = value ?? defaultValue(field);
    const hint = fieldHint(field.path);
    const unit = fieldUnit(field);

    const control = (() => {
        if (!included) return null;
        switch (field.valueKind) {
            case 'boolean':
                return (
                    <SegmentedToggle
                        size="sm"
                        value={value ? 'on' : 'off'}
                        options={[
                            { value: 'on', label: CONFIG_TEMPLATE_COPY.ON },
                            { value: 'off', label: CONFIG_TEMPLATE_COPY.OFF },
                        ]}
                        onChange={(next) => onValueChange(next === 'on')}
                    />
                );
            case 'number':
                return (
                    <div className="flex items-center" style={{ gap: 8 }}>
                        <SettingsInput
                            type="number"
                            value={typeof value === 'number' ? String(value) : ''}
                            onChange={(next) =>
                                onValueChange(next === '' ? '' : Number(next))
                            }
                            placeholder="0"
                            width={116}
                        />
                        {unit ? (
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {unit}
                            </span>
                        ) : null}
                    </div>
                );
            case 'time':
                return (
                    <div className="flex items-center" style={{ gap: 8 }}>
                        <SettingsInput
                            type="time"
                            value={typeof value === 'string' ? value : ''}
                            onChange={onValueChange}
                            placeholder={fieldTimePlaceholder(field)}
                            width={96}
                        />
                        <span
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: 'var(--cpm-text-secondary)',
                            }}
                        >
                            {CONFIG_TEMPLATE_COPY.CLOCK_24}
                        </span>
                    </div>
                );
            case 'string':
                return (
                    <SettingsInput
                        value={typeof value === 'string' ? value : ''}
                        onChange={onValueChange}
                        placeholder={fieldTextPlaceholder(field, clusterTitle)}
                    />
                );
            case 'enum':
                return (
                    <CpmFilterDropdown
                        label=""
                        options={(field.enumValues ?? []).map((item) => ({
                            value: item,
                            label: item,
                        }))}
                        selected={typeof value === 'string' && value ? [value] : []}
                        onChange={(next) => onValueChange(next[0] ?? '')}
                        multi={false}
                        includeEmptyOption={false}
                        emptyLabel="Select"
                        portal
                        className="!max-w-[260px]"
                    />
                );
            case 'string[]':
                if (field.enumValues?.length) {
                    return (
                        <ChipSet
                            options={field.enumValues}
                            selected={Array.isArray(value) ? (value as string[]) : []}
                            onChange={onValueChange}
                        />
                    );
                }
                return (
                    <FreeStringList
                        value={Array.isArray(value) ? (value as string[]) : []}
                        placeholder={fieldListPlaceholder(field)}
                        onChange={onValueChange}
                    />
                );
            case 'object[]': {
                const keys = (field.itemFields ?? []).map((item) => item.key).join(',');
                if (keys === 'category,subcategories') {
                    return (
                        <ExpenseCategoryList
                            value={(Array.isArray(value) ? value : []) as ExpenseCategoryRow[]}
                            onChange={onValueChange}
                        />
                    );
                }
                if (
                    keys === 'name' ||
                    (field.itemFields?.length === 1 && field.itemFields[0].key === 'name')
                ) {
                    return (
                        <NameRows
                            value={(Array.isArray(value) ? value : []) as { name: string }[]}
                            placeholder={fieldListPlaceholder(field)}
                            onChange={onValueChange}
                        />
                    );
                }
                return (
                    <p className="m-0 text-[12px] text-[var(--cpm-text-secondary)]">
                        Unsupported object list
                    </p>
                );
            }
            case 'weekdayTimings': {
                const rows = (Array.isArray(value) ? value : []) as WeekdayTimingRow[];
                return (
                    <WeekdayTimingsEditor
                        value={rows}
                        includedDays={rows.map((row) => row.day)}
                        onToggleDay={(day, nextIncluded) => {
                            if (nextIncluded) {
                                onValueChange([
                                    ...rows,
                                    {
                                        day,
                                        openingTime: '1970-01-01T10:00:00.000Z',
                                        closingTime: '1970-01-01T22:00:00.000Z',
                                    },
                                ]);
                                return;
                            }
                            onValueChange(rows.filter((row) => row.day !== day));
                        }}
                        onChangeRow={(day, patch) => {
                            onValueChange(
                                rows.map((row) => (row.day === day ? { ...row, ...patch } : row))
                            );
                        }}
                    />
                );
            }
            default:
                return null;
        }
    })();

    return (
        <div
            className="flex items-start hover:bg-[var(--cpm-purple-tint-3)]"
            style={{
                gap: 14,
                padding: '11px 13px',
                borderBottom: '1px solid var(--cpm-border-default)',
            }}
        >
            <div
                className="flex-none flex items-start"
                style={{ width: 224, gap: 7, paddingTop: 6 }}
            >
                <CpmCheckbox
                    label={field.label}
                    checked={included}
                    onChange={() => onIncludedChange(!included, fallback)}
                />
                {hint ? (
                    <CpmTooltip content={hint} align="start" fit>
                        <span
                            className="inline-flex items-center"
                            style={{ paddingTop: 2, color: 'var(--cpm-text-tertiary)' }}
                        >
                            <InfoIcon />
                        </span>
                    </CpmTooltip>
                ) : null}
            </div>
            <div className="flex-1 min-w-0" style={{ paddingTop: 3 }}>
                {control}
            </div>
            {changed ? (
                <span
                    className="flex-none uppercase"
                    style={{
                        paddingTop: 8,
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        color: 'var(--cpm-brand-purple)',
                    }}
                >
                    Changed
                </span>
            ) : null}
        </div>
    );
}
